import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Loader2, Send, ShieldOff } from 'lucide-react';
import { FollowUpTemplate } from './FollowUpTemplate';

const STATUS_LABEL = { sent: 'Sent', delivered: 'Delivered', read: 'Read', failed: 'Failed' };

const Bubble = ({ m }) => {
  const out = m.direction === 'out';
  return (
    <div className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${out ? 'bg-green-600 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
        <p className="whitespace-pre-wrap break-words">{m.text}</p>
        <div className={`flex items-center gap-2 mt-1 text-[10px] ${out ? 'text-green-100' : 'text-muted-foreground'}`}>
          <span>{new Date(m.ts).toLocaleString()}</span>
          {out && m.status && <span>· {STATUS_LABEL[m.status] || m.status}</span>}
          {out && m.sent_by && <span>· {m.sent_by}</span>}
          {m.opt_out_triggered && <span className="text-red-500 font-medium">· Opted out</span>}
        </div>
      </div>
    </div>
  );
};

export const ThreadView = ({ thread, loading, onSend, sending, onFollowUp }) => {
  const [text, setText] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length]);

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground" data-testid="inbox-no-selection">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Select a conversation'}
      </div>
    );
  }

  const submit = async () => {
    if (!text.trim()) return;
    if (await onSend(text.trim())) setText('');
  };

  return (
    <div className="flex flex-col h-full" data-testid="inbox-thread">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-sm" data-testid="inbox-thread-phone">{thread.phone}</p>
          <p className="text-xs text-muted-foreground">
            {thread.user ? `${thread.user.full_name || ''} ${thread.user.email}`.trim() : 'Not linked to a ParachPay account'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {thread.user && !thread.user.whatsapp_opt_in && (
            <Badge variant="outline" className="text-red-500 border-red-500/40" data-testid="inbox-opted-out-badge">
              <ShieldOff className="w-3 h-3 mr-1" />Opted out
            </Badge>
          )}
          <Badge variant="outline" className={thread.window_open ? 'text-green-600 border-green-600/40' : 'text-muted-foreground'} data-testid="inbox-window-badge">
            {thread.window_open ? `Window open until ${new Date(thread.window_expires_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}` : 'Window closed'}
          </Badge>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="inbox-messages">
        {thread.messages.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-border">
        {thread.window_open ? (
          <div className="flex gap-2 items-end">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              placeholder="Type a reply (Enter to send, Shift+Enter for new line)"
              rows={2}
              maxLength={4096}
              className="rounded-xl resize-none"
              data-testid="inbox-reply-input"
            />
            <Button onClick={submit} disabled={sending || !text.trim()} className="rounded-xl h-10" data-testid="inbox-reply-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        ) : (
          <FollowUpTemplate thread={thread} onFollowUp={onFollowUp} />
        )}
      </div>
    </div>
  );
};
